<template lang="pug">
  div
    .yz-switch(:class="{ 'yz-switch--disabled': disabled, 'yz-switch--invalid': hasError, ['yz-switch--' + size]: true }")
      label.yz-switch__container
        input.yz-switch__input(:id="`switch-${_uid}`" type="checkbox" :checked="isChecked" :name="computedName" :disabled="disabled" @change="onChange" @focus="onFocus" @blur="onBlur" ref="switch")
        .yz-switch__switch
          .yz-switch__slider
            .yz-switch__icon(v-if="$slots.icon")
              slot(name="icon")
    .yz-regular-body-xs.error--text.mt-2(v-if="computedErrorMessage") {{ computedErrorMessage }}
</template>

<script>
export default {
  name: 'YzSwitch',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    requiredErrorMessage: {
      type: String,
      default: 'This field is required'
    },
    size: {
      type: String,
      default: 'lg',
      validator: value => ['xs', 'sm', 'md', 'lg'].includes(value)
    },
    // Add rules prop for more flexible validation
    rules: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      hasBeenValidated: false,
      internalErrorMessages: []
    }
  },
  computed: {
    isChecked() {
      return this.value;
    },
    computedName() {
      return `switch-${this._uid}`;
    },
    // Merge required validation into rules array
    mergedRules() {
      const rules = [...this.rules];
      
      // If required is true, prepend the required validation rule
      if (this.required) {
        rules.unshift(v => v === true || this.requiredErrorMessage);
      }
      
      return rules;
    },
    computedErrorMessage() {
      // Simplified - only check merged rules validation results
      if (this.internalErrorMessages.length > 0) {
        return this.hasBeenValidated ? this.internalErrorMessages[0] : '';
      }
      return '';
    },
    hasError() {
      return !!this.computedErrorMessage;
    }
  },
  watch: {
    value: {
      handler() {
        // Only validate if already been validated (for reactive validation)
        if (this.hasBeenValidated) {
          this.validate();
        }
      },
      immediate: false
    },
    mergedRules: {
      handler() {
        this.validate();
      },
      immediate: false,
      deep: true
    }
  },
  mounted() {
    // Register with parent v-form if it exists
    this.form = this.getForm();
    if (this.form && this.form.register) {
      this.form.register(this);
    }
  },
  beforeDestroy() {
    // Unregister from parent v-form
    if (this.form && this.form.unregister) {
      this.form.unregister(this);
    }
  },
  methods: {
    onChange() {
      if (!this.disabled) {
        this.$emit('input', !this.value);
        this.$emit('change', !this.value);
        this.validate(true);
      }
    },
    onFocus(event) {
      this.$emit('focus', event);
    },
    onBlur(event) {
      this.$emit('blur', event);
      this.validate();
    },
    focus() {
      this.$refs.switch.focus();
    },
    blur() {
      this.$refs.switch.blur();
    },

    // Main validation method that v-form will call
    validate(force = false) {
      if (force) {
        this.hasBeenValidated = true;
      }

      // If not yet validated and not forced, don't show errors
      if (!this.hasBeenValidated && !force) {
        return true;
      }

      this.hasBeenValidated = true;
      this.internalErrorMessages = [];

      // Run merged rules (includes required validation if set)
      if (this.mergedRules.length > 0) {
        for (const rule of this.mergedRules) {
          const result = typeof rule === 'function' ? rule(this.value) : rule;
          if (result !== true) {
            this.internalErrorMessages.push(result || 'Invalid value');
          }
        }

        if (this.internalErrorMessages.length > 0) {
          return false;
        }
      }

      return true;
    },

    // Just clear validation errors without resetting value
    resetValidation() {
      this.hasBeenValidated = false;
      this.internalErrorMessages = [];
    },

    // Helper method to find parent v-form
    getForm() {
      let parent = this.$parent;

      while (parent) {
        // Check if it's a v-form by name or by checking for form methods
        if (parent.$options.name === 'v-form' || (parent.register && parent.unregister && parent.validate)) {
          return parent;
        }

        // Also check if parent has a form ref (for custom form wrappers)
        if (parent.$refs && parent.$refs.form &&
            parent.$refs.form.register && parent.$refs.form.unregister) {
          return parent.$refs.form;
        }

        parent = parent.$parent;
      }

      return null;
    }
  }
}
</script>

<style lang="scss" scoped>
.yz-switch {
  position: relative;
  display: inline-flex;
  align-items: center;
  font-family: $body-font-family;
  min-width: fit-content;

  &--xs {
    .yz-switch__switch {
      width: 28px;
      height: 16px;
    }

    .yz-switch__slider {
      width: 12px;
      height: 12px;
    }

    .yz-switch__input:checked ~ .yz-switch__switch .yz-switch__slider {
      transform: translateX(calc(28px - 12px - 4px));
    }
  }

  &--sm {
    .yz-switch__switch {
      width: 36px;
      height: 20px;
    }

    .yz-switch__slider {
      width: 16px;
      height: 16px;
    }

    .yz-switch__input:checked ~ .yz-switch__switch .yz-switch__slider {
      transform: translateX(calc(36px - 16px - 4px));
    }
  }

  &--md {
    .yz-switch__switch {
      width: 48px;
      height: 24px;
    }

    .yz-switch__slider {
      width: 20px;
      height: 20px;
    }

    .yz-switch__input:checked ~ .yz-switch__switch .yz-switch__slider {
      transform: translateX(calc(48px - 20px - 4px));
    }
  }

  &--lg {
    .yz-switch__switch {
      width: 52px;
      height: 32px;
    }

    .yz-switch__slider {
      width: 28px;
      height: 28px;
    }

    .yz-switch__input:checked ~ .yz-switch__switch .yz-switch__slider {
      transform: translateX(calc(52px - 28px - 4px));
    }
  }

  &__container {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    min-width: fit-content;

    &:hover .yz-switch__switch {
      background-color: var(--yz-neutral-500);
    }

    &:hover .yz-switch__input:checked ~ .yz-switch__switch {
      background-color: #4AC66C;
    }
  }

  &__input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;

    &:checked ~ .yz-switch__switch {
      background-color: var(--yz-success);
    }

    &:focus ~ .yz-switch__switch {
      box-shadow: 0 0 0 2px var(--yz-neutral-300);
    }
  }

  &__switch {
    position: relative;
    display: flex;
    align-items: center;
    border-radius: 29px;
    background-color: var(--yz-neutral-400);
    transition: all 0.3s ease;
    box-sizing: border-box;
    padding: 2px;
    flex-shrink: 0;
  }

  &__slider {
    position: relative;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: var(--yz-white);
    transition: transform 0.3s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__icon {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: var(--yz-neutral-600);
    font-size: 12px;

    // Size-specific icon sizing
    .yz-switch--xs & {
      font-size: 8px;
    }

    .yz-switch--sm & {
      font-size: 10px;
    }

    .yz-switch--md & {
      font-size: 12px;
    }

    .yz-switch--lg & {
      font-size: 16px;
    }
  }


  &--disabled {
    .yz-switch__container {
      cursor: not-allowed;
    }

    .yz-switch__switch {
      background-color: var(--yz-neutral-300);
    }

    .yz-switch__container:hover .yz-switch__switch {
      background-color: var(--yz-neutral-300);
    }

    .yz-switch__container:hover .yz-switch__input:checked ~ .yz-switch__switch {
      background-color: var(--yz-neutral-400);
    }

    .yz-switch__input:checked ~ .yz-switch__switch {
      background-color: var(--yz-neutral-400);
    }

    .yz-switch__slider {
      background-color: var(--yz-neutral-100);
    }

  }

  &--invalid {
    .yz-switch__switch {
      background-color: #ea3d37;
    }

    .yz-switch__container:hover .yz-switch__switch {
      background-color: var(--yz-danger-700);
    }

    .yz-switch__input:checked ~ .yz-switch__switch {
      background-color: var(--yz-danger-500);
    }

    .yz-switch__container:hover .yz-switch__input:checked ~ .yz-switch__switch {
      background-color: var(--yz-danger-700);
    }
  }
}
</style>